<template>
  <!-- FB訂單詳細頁 -->
  <v-container class="booking-container px-0" fluid>
    <div v-if="fromTngMiniProgram" class="tng-header">
      <v-icon class="back-btn" @click="goToTngOrderList">mdi-chevron-left</v-icon>
    </div>
    <div class="order-detail-wrapper">
      <div class="order-content__left">
        <!-- 訂購內容 -->
        <v-sheet class="order-content__block" elevation="0" rounded>
          <p class="sheet-title">{{ $t('all_orders') }}</p>

          <div class="order-content__block__content">
            <FunBookV2OrderDetailSheet :order="order" />
          </div>
        </v-sheet>

        <!-- 取消與改時間 -->
        <v-sheet v-if="showPolicyHelp" class="order-content__block" elevation="0" rounded>
          <p class="sheet-title">{{ $t('all_cancellation_field_title') }}</p>

          <div class="order-content__block__content">
            <OrderMutationPolicySheet :order="order" />
          </div>
        </v-sheet>

        <!-- 現場付款 不會有線上付款帳單 -->
        <template v-if="!isPaidOnsite">
          <!-- 帳單內容 -->
          <v-sheet class="order-content__block" elevation="0" rounded>
            <p class="sheet-title">{{ $t('billing_title') }}</p>

            <div class="order-content__block__content">
              <PaymentDetailSheet :order="order" :show-comment-info="false" />
            </div>
          </v-sheet>

          <!-- 退費明細 -->
          <v-sheet v-if="isCancel" class="order-content__block" elevation="0" rounded>
            <p class="sheet-title">{{ refundSheetTitle }}</p>

            <div class="order-content__block__content">
              <refund-detail-sheet :order="order" />
            </div>
          </v-sheet>
        </template>

        <!-- 幫助內容 -->
        <v-sheet v-if="showHelpSheet" class="order-content__block" elevation="0" rounded>
          <p class="sheet-title">{{ $t('booking_detail_help') }}</p>

          <div class="order-content__block__content">
            <order-detail-help-sheet :order="order" :from-tng-mini-program="fromTngMiniProgram" :is-booking-engine="isBookingEngine" />
          </div>
        </v-sheet>

        <!-- 刪除 -->
        <v-sheet v-if="showDeleteSheet" class="order-content__block" elevation="0" rounded>
          <div class="order-content__block__content">
            <div class="wd-px-5 wd-py-5">
              <v-btn class="large--btn" width="100%" height="40" color="orange50" variant="outlined" @click="openDeleteModal">
                {{ $t('delete_order_button') }}
              </v-btn>
            </div>
          </div>
        </v-sheet>
        <!-- 付款連結 -->
        <FixedBottomSubmitBtn
          v-if="showPaymentLink"
          :is-hint-shown="isPaymentExpired"
          :hint-text="paymentExpiredHint"
          :is-submit-btn-disabled="isPaymentExpired"
          :submit-btn-text="paymentSubmitText"
          @click="goToPayment"
        />
      </div>

      <!-- -------------分界-----------------  -->

      <div class="order-content__right">
        <!-- 訂單狀態資訊 -->
        <v-sheet class="order-content__block order-status-sheet" elevation="0" rounded>
          <OrderStatusInfoSheet :order="order" />
        </v-sheet>

        <!-- 兌換資訊 -->
        <v-sheet v-if="showRedeemSheet" class="order-content__block" elevation="0" rounded>
          <FunBookV2OrderRedeem :order="order" />
        </v-sheet>

        <!-- 行前注意事項 -->
        <OrderPreparationNotice v-if="order.product.attn" class="order-content__block" :notice="order.product.attn" />

        <AppDownloadSheet v-if="showAppDownload" />
      </div>
    </div>

    <!-- 刪除訂單視窗 -->
    <delete-order-modal ref="visibleOrderModal" :order-id="orderID" />
  </v-container>
</template>

<script>
import { syncRefs } from '@vueuse/core'

import FixedBottomSubmitBtn from '@/components/FixedBottomSubmitBtn.vue'
import FunBookV2OrderDetailSheet from '@/components/FunBookV2OrderDetailSheet.vue'
import PaymentDetailSheet from '@/components/page/orders/payment-detail-sheet.vue'
import OrderStatusInfoSheet from '@/components/page/orders/order-status-info-sheet.vue'
import FunBookV2OrderRedeem from '@/components/FunBookV2OrderRedeem.vue'
import OrderPreparationNotice from '@/components/page/orders/OrderPreparationNotice.vue'
import OrderDetailHelpSheet from '@/components/page/orders/order-detail-help-sheet.vue'
import RefundDetailSheet from '@/components/page/orders/refund-detail-sheet.vue'
import AppDownloadSheet from '@/components/AppDownloadSheet.vue'
import DeleteOrderModal from '@/components/dialog/orders/delete-order-modal.vue'
import OrderMutationPolicySheet from '@/components/OrderMutationPolicySheet.vue'

import { OrderCommonStatus } from '@/view-models/order'
import { usePlatformStore } from '@/store-pinia/platform'
import { useOrderPaymentStore } from '@/store-pinia/order-payment'

import { useOrderFlags } from '@/composition/useOrder'
import { useAnonymousOrder } from '@/composition/useAnonymousOrder'
import { useBaseMessage } from '@/composition/useMessage'
import { useIntlNumber } from '@/composition/useIntl'

export default defineComponent({
  name: 'FunBookBranchIdOrdersInfoOrderId',
  components: {
    FunBookV2OrderDetailSheet,
    PaymentDetailSheet,
    OrderStatusInfoSheet,
    FunBookV2OrderRedeem,
    OrderDetailHelpSheet,
    RefundDetailSheet,
    DeleteOrderModal,
    OrderPreparationNotice,
    AppDownloadSheet,
    OrderMutationPolicySheet,
    FixedBottomSubmitBtn,
  },
  props: {
    order: { type: Object, default: () => ({ product: {} }) },
  },
  setup(props, ctx) {
    const nuxtApp = useNuxtApp()
    const router = useRouter()
    const route = useRoute()
    const { $store: store, $auth, $dayjs } = nuxtApp
    const platformStore = usePlatformStore()
    const { getLocaleNumber } = useIntlNumber()

    const expiredMessages = reactive({
      header: nuxtApp.$i18n.t('payment_failed_dialog_title'),
      message: nuxtApp.$i18n.t('payment_exprired_dialog_content'),
    })

    const { showMessageModal: showExpiredModal } = useBaseMessage({
      data: toRefs(expiredMessages),
    })

    const visibleOrderModal = ref(null)
    const orderID = computed(() => route.params.order_id)
    const fromTngMiniProgram = computed(() => store.getters.fromTngMiniProgram)
    const order = computed(() => props.order)

    const { isAnonymousOrder, checkOrderMutation } = useAnonymousOrder(order)

    const loggedInUser = computed(() => $auth.user)
    const isCancel = computed(() => order.value.order_status === OrderCommonStatus.Cancel)
    const showRedeemSheet = computed(() => order.value.order_status === OrderCommonStatus.BookingSuccess && isPaymentPaid.value)
    /** 幫助 & 退改區塊 */
    const showPolicyHelp = computed(() => showRedeemSheet.value && !isCancel.value)
    const showHelpSheet = computed(() => !isMovie.value && order.value.branch_phone)
    const showDeleteSheet = computed(() => !showRedeemSheet.value && !isAnonymousOrder.value && isPaymentPaid.value)

    const isBookingEngine = computed(() => store.getters.isBookingEngine)

    const { isPaidOnsite, isPaymentPaid, isPaymentExpired, isDeferPayment, isMovie } = useOrderFlags(order)
    const showAppDownload = computed(() => !fromTngMiniProgram.value && !platformStore.isFunBookV2)
    const refundSheetTitle = computed(() => (isDeferPayment.value ? nuxtApp.$i18n.t('all_cancel') : nuxtApp.$i18n.t('refund_detail_title')))
    // 付款連結
    const paymentSubmitText = computed(() =>
      nuxtApp.$i18n.t('paid_button', { price: `${order.value.symbol_code} ${getLocaleNumber(order.value?.total_amount) ?? 0}` })
    )
    const paymentExpiredHint = computed(() => nuxtApp.$i18n.t('order_detail_payment_expired_hint'))
    const showPaymentLink = computed(() => isDeferPayment.value && !isPaymentPaid.value && !isCancel.value)
    const { orderDetail } = storeToRefs(useOrderPaymentStore())
    syncRefs(order, orderDetail)

    function goToPayment() {
      if (checkPaymentExpired()) {
        showExpiredModal()
        return
      }

      const branchId = route.params.branch_id
      router.push(
        nuxtApp.$localePath({
          name: 'funbook-branch_id-orders-info-order_id-payment',
          params: { branch_id: branchId, order_id: order.value.id },
          query: { t: route.query.t },
        })
      )
    }

    function checkPaymentExpired() {
      return $dayjs(order.value.booking_time).add(1, 'day').isBefore($dayjs())
    }

    function openDeleteModal() {
      try {
        if (!checkOrderMutation()) {
          return
        }
        if (order.value.id) {
          visibleOrderModal.value.showModal()
        }
      } catch (error) {
        console.error(error)
      }
    }

    function goToTngOrderList() {
      location.replace('/en/tng/1?from=tng&type=tng-order-list')
    }

    return {
      orderID,
      loggedInUser,
      refundSheetTitle,
      isCancel,
      showRedeemSheet,
      showPolicyHelp,
      showDeleteSheet,
      showHelpSheet,
      fromTngMiniProgram,
      isPaymentPaid,
      isPaymentExpired,
      showPaymentLink,
      paymentSubmitText,
      paymentExpiredHint,
      isBookingEngine,
      visibleOrderModal,
      openDeleteModal,
      goToTngOrderList,
      isPaidOnsite,
      showAppDownload,
      goToPayment,
    }
  },
})
</script>

<style lang="scss" src="@/assets/sass/pages/order-detail-page.scss"></style>
<style scoped lang="scss">
.tng-header {
  padding: 10px 0px 10px 10px;
  margin-top: -10px;
  background-color: white;
}
.back-btn {
  font-size: 30px;
}
</style>
